import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { IDS } from "@/lib/testIds";
import { getQueue } from "@/lib/offline";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  House, QrCode, Package as PackageIcon, ShoppingCart, ClipboardText,
  Calculator, Gear, SignOut, Sun, Moon, WifiSlash, WifiHigh, ArrowsClockwise, List, X, ListChecks,
  BellRinging, ForkKnife, LockKey,
} from "@phosphor-icons/react";

export default function Layout() {
  const { user, logout, theme, toggleTheme } = useAuth();
  const nav = useNavigate();
  const [online, setOnline] = useState(navigator.onLine);
  const [queueLen, setQueueLen] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const q = await getQueue();
      if (!cancelled) setQueueLen(q.length);
    };
    refresh();
    const t = setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const syncQueue = async () => {
    const q = await getQueue();
    if (q.length === 0) return toast.info("Nothing to sync");
    // Group by session_id. Special marker "meal_action" is not a real session —
    // it flags a queued meal-record scan action (POST /meals/action per entry).
    const bySession = {};
    q.forEach((e) => {
      bySession[e.session_id] = bySession[e.session_id] || [];
      bySession[e.session_id].push(e);
    });
    let ok = 0;
    let bad = 0;
    const failures = [];
    for (const [sid, items] of Object.entries(bySession)) {
      if (sid === "meal_action") {
        // Send each meal-action individually
        for (const item of items) {
          try {
            await api.post("/meals/action", { ...item, source: "offline_sync" });
            ok += 1;
          } catch (e) {
            bad += 1;
            failures.push({ item, error: formatApiError(e) });
          }
        }
      } else {
        try {
          const { data } = await api.post(`/inventory/sessions/${sid}/sync`, { session_id: sid, entries: items });
          ok += data.synced;
          bad += data.conflicts.length;
          if (data.conflicts.length) failures.push(...data.conflicts);
        } catch (e) {
          bad += items.length;
          failures.push({ session_id: sid, error: formatApiError(e), count: items.length });
        }
      }
    }
    if (ok > 0) {
      const { clearQueue } = await import("@/lib/offline");
      await clearQueue();
    }
    // Report sync failures to backend so administrators can be notified.
    if (bad > 0) {
      try {
        await api.post("/notifications/sync-failure", { failures: failures.slice(0, 50), failed_count: bad });
      } catch (err) {
        // Notifications route may 404 during rolling deploys — sync outcome
        // is already surfaced to the user via the toast below.
        if (process.env.NODE_ENV !== "production") console.debug("[sync] sync-failure ingest skipped:", err?.message);
      }
      toast.warning(`Synced ${ok} entries, ${bad} failed`);
    } else {
      toast.success(`Synced ${ok} entries`);
    }
  };

  const isAdmin = user?.role === "admin" || user?.role === "system_owner";
  const isSuperOrAdmin = user?.role === "admin" || user?.role === "supervisor" || user?.role === "system_owner";
  const isDemoUser = !!user?.is_demo;

  const navItems = [
    { to: "/", icon: House, label: "Dashboard", tid: IDS.navDashboard, end: true },
    { to: "/scan", icon: QrCode, label: "Scan", tid: IDS.navScan },
    { to: "/items", icon: PackageIcon, label: "Dry Goods Items", tid: IDS.navItems },
    { to: "/meal-items", icon: ForkKnife, label: "Meal Items", tid: "nav-meal-items" },
    { to: "/reorder", icon: ShoppingCart, label: "Reorder", tid: IDS.navReorder },
    ...(isDemoUser ? [] : [{ to: "/sessions", icon: ClipboardText, label: "Sessions", tid: IDS.navSessions }]),
    ...(isDemoUser ? [] : [{ to: "/transactions", icon: ListChecks, label: "Transactions", tid: "nav-transactions" }]),
    ...(isDemoUser ? [] : [{ to: "/notifications", icon: BellRinging, label: "Notifications", tid: IDS.navNotifications }]),
    ...(!isDemoUser && isSuperOrAdmin ? [{ to: "/admin", icon: Gear, label: "Admin", tid: IDS.navAdmin }] : []),
  ];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* iter20: Demo Mode banner — visible on every authenticated page */}
      {isDemoUser && (
        <div
          data-testid="demo-mode-banner"
          className="w-full bg-yellow-500 text-black text-center text-[11px] font-bold uppercase tracking-widest py-1 border-b border-yellow-600"
        >
          Demo Mode — Read Only · Sign in as an operator to make changes
        </div>
      )}
      {/* Top bar */}
      <header className="h-14 border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-40 flex items-center px-3 md:px-6 gap-3">
        <button
          onClick={() => setDrawerOpen((v) => !v)}
          className="md:hidden w-9 h-9 grid place-items-center border border-border rounded-sm hover:bg-muted"
          aria-label="Menu"
        >
          {drawerOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 grid place-items-center bg-primary text-primary-foreground">
            <PackageIcon size={18} weight="bold" />
          </div>
          <div className="hidden sm:block">
            <div className="font-display font-black text-sm tracking-tight leading-none">INVENTORY MANAGEMENT SYSTEM</div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-muted-foreground leading-none mt-0.5">Ops</div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div
            data-testid={IDS.offlineBanner}
            className={`flex items-center gap-1.5 px-2.5 h-8 border rounded-sm text-[11px] font-mono ${
              online
                ? "border-border text-muted-foreground"
                : "border-yellow-500/60 text-yellow-500 bg-yellow-500/10"
            }`}
            title={online ? "Online" : "Offline"}
          >
            {online ? <WifiHigh size={14} weight="bold" /> : <WifiSlash size={14} weight="bold" />}
            <span className="hidden sm:inline">{online ? "ONLINE" : "OFFLINE"}</span>
          </div>
          {queueLen > 0 && (
            <button
              data-testid={IDS.syncButton}
              onClick={syncQueue}
              className="btn-hard flex items-center gap-1.5 px-2.5 h-8 border border-primary text-primary rounded-sm text-[11px] font-mono font-bold hover:bg-primary hover:text-primary-foreground"
              title="Sync pending entries"
            >
              <ArrowsClockwise size={14} weight="bold" />
              <span>{queueLen} PENDING</span>
            </button>
          )}
          <button
            data-testid={IDS.themeToggle}
            onClick={toggleTheme}
            className="w-8 h-8 grid place-items-center border border-border rounded-sm hover:bg-muted"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
          </button>
          <div className="hidden sm:flex items-center gap-2 text-xs pl-2 border-l border-border h-8">
            <div className="text-right leading-tight">
              <div className="font-semibold text-[12px]">{user?.name}</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{user?.role}</div>
            </div>
          </div>
          <NavLink
            to="/account/change-password"
            data-testid="nav-change-password"
            className="w-8 h-8 grid place-items-center border border-border rounded-sm hover:bg-muted"
            title="Change password"
          >
            <LockKey size={16} weight="bold" />
          </NavLink>
          <button
            data-testid={IDS.logoutBtn}
            onClick={doLogout}
            className="w-8 h-8 grid place-items-center border border-border rounded-sm hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
            title="Sign out"
          >
            <SignOut size={16} weight="bold" />
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex md:w-56 border-r border-border bg-card flex-col">
          <nav className="flex flex-col p-2 gap-0.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 h-10 rounded-sm text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted"
                  }`
                }
              >
                <item.icon size={18} weight="bold" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto p-3 border-t border-border text-[10px] text-muted-foreground font-mono tracking-widest uppercase">
            v1.0 · {new Date().getFullYear()}
          </div>
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div className="md:hidden fixed inset-0 top-14 z-30 bg-background/95 backdrop-blur-sm">
            <nav className="flex flex-col p-3 gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`mobile-${item.tid}`}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 h-12 rounded-sm text-base font-medium ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    }`
                  }
                >
                  <item.icon size={22} weight="bold" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0 pb-20 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 border-t border-border bg-card z-30 grid grid-cols-5">
        {[
          { to: "/", icon: House, label: "Home", end: true },
          { to: "/items", icon: PackageIcon, label: "Items" },
          { to: "/scan", icon: QrCode, label: "SCAN", primary: true },
          { to: "/reorder", icon: ShoppingCart, label: "Reorder" },
          { to: "/sessions", icon: ClipboardText, label: "Sessions" },
        ].map((it) => {
          const bottomNavColor = (isActive) => {
            if (it.primary) return "bg-primary text-primary-foreground";
            if (isActive) return "text-primary";
            return "text-muted-foreground";
          };
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold tracking-widest uppercase ${bottomNavColor(isActive)}`
              }
            >
              <it.icon size={22} weight={it.primary ? "fill" : "bold"} />
              <span>{it.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
